import { jsx as _jsx } from "react/jsx-runtime";
import * as O from 'fp-ts/Option';
import { pipe } from 'fp-ts/function';
import * as React from 'react';
import { useRefLazy } from '../hooks';
import { useLayoutDimension } from '../hooks/useLayoutDimension/useLayoutDimension';
import { Browser } from '../library/browser'; // -------------------------------------------------------------------------------------
// BrowserContextRun
// -------------------------------------------------------------------------------------

const BrowserContextRunInit = {
  bodyScroll: {
    count: 0,
    fixed: false
  }
};

const browserContextRunReducer = (state, action) => {
  switch (action.type) {
    case 'bodyScroll':
      {
        const count = action.payload === 'auto' ? Math.max(0, state.bodyScroll.count - 1) : state.bodyScroll.count + 1;
        return Object.assign(Object.assign({}, state), {
          bodyScroll: {
            count,
            fixed: count === 0 ? false : state.bodyScroll.fixed || action.payload === 'fixed'
          }
        });
      }
  }
};

export const useBrowserContextRunState = () => {
  const [state, dispatch] = React.useReducer(browserContextRunReducer, BrowserContextRunInit);
  const firstRun = React.useRef(true);
  React.useEffect(() => {
    if (firstRun.current) {
      firstRun.current = false;
      return;
    }

    if (state.bodyScroll.count > 0) {
      document.body.style.overflowY = 'hidden';
      document.body.style.touchAction = 'none';
      document.body.style.width = '100%';

      if (state.bodyScroll.fixed) {
        document.body.style.position = 'fixed';
      }
    } else {
      document.body.style.overflowY = 'auto';
      document.body.style.touchAction = 'auto';
      document.body.style.position = 'static';
      document.body.style.width = 'auto';
    }
  }, [state.bodyScroll.count, state.bodyScroll.fixed]);
  return {
    state,
    dispatch
  };
}; // -------------------------------------------------------------------------------------
// RootContext
// -------------------------------------------------------------------------------------

export const RootContext = /*#__PURE__*/React.createContext({
  env: 'dev',
  canUseDOM: true,
  headWrapper: O.none
});
export const useRootContext = () => React.useContext(RootContext); // -------------------------------------------------------------------------------------
// BrowserContext
// -------------------------------------------------------------------------------------

const BrowserContext = /*#__PURE__*/React.createContext({
  layout: O.none,
  browserParser: O.none,
  run: {
    state: BrowserContextRunInit,
    dispatch: () => BrowserContextRunInit
  }
});
export const useBrowserContext = () => React.useContext(BrowserContext);
export const useBrowserContextRunDispatch = () => {
  return React.useContext(BrowserContext).run.dispatch;
};
export const useCurrentBreakpoint = init => pipe(useBrowserContext().layout, O.fold(() => init, l => l.breakpoint));
export const useCurrentBreakpointFamily = init => pipe(useBrowserContext().layout, O.fold(() => init, l => l.breakpointFamily));
export const BrowserProvider = ({
  children,
  canUseDOM
}) => {
  const browserParser = useRefLazy(() => canUseDOM ? O.some(Browser.getParser(window.navigator.userAgent)) : O.none);
  const layout = useLayoutDimension();
  const rootRun = useBrowserContextRunState();
  return _jsx(BrowserContext.Provider, Object.assign({
    value: {
      layout,
      browserParser: browserParser.current,
      run: rootRun
    }
  }, {
    children: children
  }));
};
//# sourceMappingURL=Root.js.map